import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../services/api";
import { User, refreshAccount } from "../types";
import { PlanCards } from "./PricingPage";
export default function BillingPage({ user }: { user: User }) {
  const [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [params] = useSearchParams();
  useEffect(() => {
    if (!params.has("success")) return;
    setMessage(
      "Checkout received. Your credits appear after Stripe confirms payment.",
    );
    let attempts = 0;
    const timer = setInterval(() => {
      refreshAccount();
      attempts++;
      if (attempts >= 8) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [params]);
  const portal = async () => {
    setBusy(true);
    setMessage("");
    try {
      const r = await apiFetch("/api/billing/portal", { method: "POST" });
      const p = await r.json();
      if (!r.ok) throw new Error(p.error || "Unable to open billing.");
      window.location.assign(p.url);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Unable to open billing.");
      setBusy(false);
    }
  };
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">YOUR PLAN. YOUR PACE.</span>
          <h1>Billing & credits</h1>
          <p>
            Clear allowances, straightforward billing, more room to connect.
          </p>
        </div>
      </div>
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      <div className="billing-summary">
        <section className="panel">
          <span className="eyebrow">AVAILABLE BALANCE</span>
          <strong className="balance-number">
            {user.credits.toLocaleString()}
            <small>credits</small>
          </strong>
          <p>
            One credit unlocks one contact. Exports and repeat views are
            included.
          </p>
        </section>
        <section className="panel">
          <span className="eyebrow">CURRENT SUBSCRIPTION</span>
          <h2>
            {user.plan === "yearly"
              ? "Annual plan"
              : user.plan === "monthly"
                ? "Monthly plan"
                : "No active plan"}
          </h2>
          <span
            className={`data-badge ${user.subscription_status === "active" ? "good" : ""}`}
          >
            {user.subscription_status.replace(/_/g, " ")}
          </span>
          <p>
            {user.plan === "yearly"
              ? "60,000 credits per successful annual payment. Standard renewal: $240/year."
              : user.plan === "monthly"
                ? "5,000 credits per successful monthly payment. $40/month."
                : "Choose monthly or annual to start unlocking contacts."}
          </p>
          {(user.plan !== "none" ||
            user.subscription_status === "past_due") && (
            <button
              className="button button-dark"
              onClick={() => void portal()}
              disabled={busy}
            >
              {busy ? "Opening Stripe…" : "Manage subscription ↗"}
            </button>
          )}
        </section>
      </div>
      {user.plan === "none" && <PlanCards user={user} />}
      <div className="panel billing-note">
        <h2>A few things to know</h2>
        <div className="faq-grid">
          <div>
            <h3>Your credits</h3>
            <p>
              Credits are added only after a successful payment. Unused credits
              carry forward while subscribed. Reopening an unlocked contact
              never costs more.
            </p>
          </div>
          <div>
            <h3>Your downloads</h3>
            <p>
              Export unlocked contacts without a separate row allowance.
              Previously unlocked contacts remain accessible after cancellation.
            </p>
          </div>
          <div>
            <h3>Your annual discount</h3>
            <p>
              Annual promotions are capped at 50% off the first year. SALES120
              is for eligible first-time customers. Renewal is $240/year.
            </p>
          </div>
          <div>
            <h3>Your subscription</h3>
            <p>
              Update payment details, view invoices, or cancel through Stripe.
              For a plan change, contact{" "}
              <a href="mailto:support@reachatlasleads.com">
                support@reachatlasleads.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
